import { useState, useEffect, useMemo } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, SessionDetail, LLMMessage, MotionDiag } from '../services/adminApi'
import { MotionLadder, MotionSummary } from '../components/sessions/MotionLadder'
import StoryDebugSidebar from '../components/chat/StoryDebugSidebar'
import styles from './SessionDetailPage.module.css'

function buildJoinUrl(detail: SessionDetail): string {
  const params = new URLSearchParams({ session_id: detail.id })
  if (detail.features?.length) params.set('features', detail.features.join(','))
  if (detail.character) params.set('character', detail.character)
  if (detail.storyPath) params.set('story', getStoryId(detail.storyPath))
  return `?${params}#chat`
}

function getStoryId(storyPath: string): string {
  const parts = storyPath.split('/')
  const filename = parts[parts.length - 1]
  return filename.replace('.yaml', '')
}

interface Props {
  sessionId: string
}

export default function SessionDetailPage({ sessionId }: Props) {
  const { apiKey } = useAdmin()

  const [detail, setDetail] = useState<SessionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)

  // The endpoint serves LIVE in-memory state, so one fetch on mount goes stale
  // as soon as the conversation continues. Poll, but with an opt-out and paused
  // while the tab is hidden.
  useEffect(() => {
    let cancelled = false
    const api = createAdminApi()

    function load(initial: boolean) {
      if (initial) { setLoading(true); setError('') }
      api.getSession(sessionId)
        .then(d => { if (!cancelled) { setDetail(d); setLoading(false) } })
        .catch(e => {
          if (cancelled) return
          // A poll failure must not blank a page already showing data: a
          // session ending mid-poll 404s, and stale beats empty.
          if (initial) { setError(e instanceof Error ? e.message : String(e)); setLoading(false) }
        })
    }

    load(true)
    if (!autoRefresh) return () => { cancelled = true }

    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load(false)
    }, 3000)
    return () => { cancelled = true; clearInterval(id) }
  }, [sessionId, apiKey, autoRefresh])

  const diagsByTurn = useMemo(() => {
    const byTurn = new Map<number, MotionDiag[]>()
    for (const d of detail?.motionDiagnostics ?? []) {
      const list = byTurn.get(d.turn) ?? []
      list.push(d)
      byTurn.set(d.turn, list)
    }
    return byTurn
  }, [detail?.motionDiagnostics])

  const totalDiags = detail?.motionDiagnostics?.length ?? 0

  // Diagnostics whose turn has no message to sit under: the ring is bounded, so
  // a long session evicts early turns while their replies stay in history, and
  // the one-shot path stamps turn 0. Shown separately rather than dropped —
  // silently losing records would misreport what the pipeline did.
  const orphanDiags = useMemo(() => {
    const turnsInHistory = new Set(
      (detail?.history ?? []).filter(m => m.role === 'assistant' && m.turn).map(m => m.turn),
    )
    return (detail?.motionDiagnostics ?? []).filter(d => !turnsInHistory.has(d.turn))
  }, [detail?.motionDiagnostics, detail?.history])

  function msgClass(msg: LLMMessage) {
    return msg.role === 'user' ? styles.userMsg : styles.assistantMsg
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <a href="#sessions" className={styles.back}>← Sessions</a>
        </header>
        <p className={styles.loading}>Loading…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <a href="#sessions" className={styles.back}>← Sessions</a>
        </header>
        <p className={styles.errorMsg}>{error}</p>
      </div>
    )
  }

  if (!detail) return null

  const completedObjectives = detail.completedObjectives
    ? Object.entries(detail.completedObjectives).filter(([, done]) => done)
    : []
  const playerStateEntries = detail.playerState ? Object.entries(detail.playerState) : []

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="#sessions" className={styles.back}>← Sessions</a>
        <h1>Session <code>{detail.id}</code></h1>
        <label className={styles.refreshToggle} title="poll every 3s; paused while this tab is hidden">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={e => setAutoRefresh(e.target.checked)}
          />
          auto-refresh
        </label>
        <a className={styles.joinBtn} href={buildJoinUrl(detail)}>Join Session</a>
      </header>

      <div className={styles.content}>
        <section className={styles.meta}>
          <div className={styles.metaItem}>
            <span>Character</span>
            <strong>{detail.character || '—'}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Story</span>
            <strong>{detail.storyPath || '—'}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Connections</span>
            <strong>{detail.connections}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Phase</span>
            <strong>{detail.storyPhase || '—'}</strong>
          </div>
          <div className={styles.metaItem}>
            <span>Turn</span>
            <strong>{detail.turnCount ?? '—'}</strong>
          </div>
          {detail.features?.length > 0 && (
            <div className={styles.metaItem}>
              <span>Features</span>
              <strong>{detail.features.join(', ')}</strong>
            </div>
          )}
        </section>

        {detail.lastMetrics && (
          <section className={styles.meta}>
            {([
              ['LLM first sentence', detail.lastMetrics.llm_first_sentence_ms],
              ['TTS fast', detail.lastMetrics.tts_fast_ms],
              ['TTS quality', detail.lastMetrics.tts_quality_ms],
              ['A2F', detail.lastMetrics.a2f_processing_ms],
              ['First frame', detail.lastMetrics.first_frame_ms],
              ['Storyteller', detail.lastMetrics.storyteller_ms],
              ['Total', detail.lastMetrics.total_ms],
            ] as Array<[string, number | undefined]>)
              .filter(([, v]) => !!v)
              .map(([label, v]) => (
                <div key={label} className={styles.metaItem}>
                  <span>{label}</span>
                  <strong>{v}ms</strong>
                </div>
              ))}
          </section>
        )}

        {detail.conversationSummary && (
          <section className={styles.section}>
            <h2>Conversation Summary</h2>
            <p className={styles.summaryText}>{detail.conversationSummary}</p>
          </section>
        )}

        {detail.storyDebug?.enabled && (
          <section className={styles.section}>
            <h2>Storyteller</h2>
            <div className={styles.storySidebarWrap}>
              <StoryDebugSidebar data={detail.storyDebug} pending={false} />
            </div>
          </section>
        )}

        {!detail.storyDebug?.enabled &&
          (completedObjectives.length > 0 || playerStateEntries.length > 0 || detail.lastStorytellerReasoning) && (
          <section className={styles.section}>
            <h2>Storyteller State</h2>
            {completedObjectives.length > 0 && (
              <div className={styles.storyBlock}>
                <h3>Completed Objectives</h3>
                <ul className={styles.objectiveList}>
                  {completedObjectives.map(([id]) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              </div>
            )}
            {playerStateEntries.length > 0 && (
              <div className={styles.storyBlock}>
                <h3>Player State</h3>
                <dl className={styles.stateList}>
                  {playerStateEntries.map(([k, v]) => (
                    <div key={k} className={styles.stateEntry}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {detail.lastStorytellerReasoning && (
              <div className={styles.storyBlock}>
                <h3>Last Reasoning</h3>
                <p className={styles.reasoningText}>{detail.lastStorytellerReasoning}</p>
              </div>
            )}
          </section>
        )}

        <section className={styles.section}>
          <h2>
            Message History ({detail.history.length})
            {totalDiags > 0 && (
              <span className={styles.sectionNote}>
                {totalDiags} motion tag{totalDiags === 1 ? '' : 's'}
              </span>
            )}
          </h2>
          {detail.history.length === 0 ? (
            <p className={styles.emptyHistory}>No messages yet.</p>
          ) : (
            <div className={styles.messages}>
              {detail.history.map((msg, i) => {
                // A reply's motions belong WITH the reply: the raw tag is
                // literally a substring of the text above it, so reading the
                // two together is the whole question an operator has.
                const diags = msg.turn ? diagsByTurn.get(msg.turn) : undefined
                return (
                  <div key={i} data-turn={msg.turn || undefined}>
                    <div className={`${styles.message} ${msgClass(msg)}`}>
                      <div className={styles.msgRole}>{msg.role}</div>
                      <div className={styles.msgContent}>{msg.content}</div>
                      {msg.type && <div className={styles.msgType}>{msg.type}</div>}
                    </div>
                    {diags && diags.length > 0 && (
                      <details className={styles.msgMotion}>
                        <summary className={styles.msgMotionHeader}>
                          <MotionSummary diags={diags} />
                        </summary>
                        {diags.map((d, k) => (
                          <MotionLadder key={k} diag={d} />
                        ))}
                      </details>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {orphanDiags.length > 0 && (
          <section className={styles.section}>
            <h2>
              Motion Pipeline — unmatched
              <span className={styles.sectionNote}>
                no message in this history carries their turn
              </span>
            </h2>
            {orphanDiags.map((d, i) => (
              <MotionLadder key={i} diag={d} />
            ))}
          </section>
        )}
      </div>
    </div>
  )
}
